import { isFieldVisible } from '../conditionalVisibility';
import { getValueAtPath } from '../values';
import type { FieldDescriptor } from '../types';
import { CheckboxField } from './CheckboxField';
import { DateField } from './DateField';
import type { FieldRenderState } from './FieldRenderState';
import { MultiSelectField } from './MultiSelectField';
import { NumberField } from './NumberField';
import { PhotoField } from './PhotoField';
import { RepeatingGroupField } from './RepeatingGroupField';
import { SectionField } from './SectionField';
import { SelectField } from './SelectField';
import { SignatureField } from './SignatureField';
import { TextField } from './TextField';

/**
 * The single place that decides, for one field: is it visible right now
 * (`isFieldVisible` — conditional DISPLAY, never validation), and which
 * widget renders it. Used at every level of the tree — top-level fields,
 * a section's children, a repeating group row's fields — so visibility and
 * widget resolution behave identically no matter how deep a field is nested.
 */
export function FieldSwitch({ field, state }: { field: FieldDescriptor; state: FieldRenderState }) {
  if (!isFieldVisible(state.rules, field.path, state.values)) {
    return null;
  }

  const value = getValueAtPath(state.values, field.path);
  const error = state.errors.find((e) => e.path === field.path)?.message;
  const onChange = (next: unknown) => state.setValue(field.path, next);

  switch (field.kind) {
    case 'text':
    case 'textarea':
      return <TextField field={field} value={value} error={error} onChange={onChange} />;
    case 'number':
      return <NumberField field={field} value={value} error={error} onChange={onChange} />;
    case 'select':
      return <SelectField field={field} value={value} error={error} onChange={onChange} />;
    case 'multiSelect':
      return <MultiSelectField field={field} value={value} error={error} onChange={onChange} />;
    case 'date':
      return <DateField field={field} value={value} error={error} onChange={onChange} />;
    case 'checkbox':
      return <CheckboxField field={field} value={value} error={error} onChange={onChange} />;
    case 'signature':
      return <SignatureField field={field} value={value} error={error} onChange={onChange} />;
    case 'photo':
      return <PhotoField field={field} value={value} error={error} onChange={onChange} />;
    case 'section':
      return <SectionField field={field} state={state} />;
    case 'repeatingGroup':
      return <RepeatingGroupField field={field} state={state} />;
    default:
      return null;
  }
}
